/**
 * Mg Theme — Product Media Gallery Component
 * (c) 2026 NAMY SOLUTIONS LLC
 *
 * Custom Element: <mg-product-gallery>
 * - Horizontal scroll-snap track (native drag/momentum/axis disambiguation)
 * - Active slide derived from scroll position (single index, no flicker)
 * - Thumbnail + dot + counter + arrow sync, aria-current
 * - Click-to-enlarge lightbox (<dialog>, native focus-trap + ESC)
 * - Listens to product:variant:changed to scroll to the variant image
 */

/* Hosted players answer to postMessage rather than properties, and the two
   dialects disagree: YouTube takes a command name, Vimeo takes a volume.
   Shared by the gallery element (pause on slide change) and the facade
   activation below, so the dialect lives in exactly one place. */
window.MgVideoFrame = window.MgVideoFrame || {
  /* vimeo: the Vimeo message; yt: the YouTube command name. */
  send: function (frame, vimeo, yt) {
    if (!frame || !frame.contentWindow) return;
    var payload = (frame.src || '').indexOf('vimeo') !== -1 ? vimeo : { event: 'command', func: yt, args: '' };
    try {
      frame.contentWindow.postMessage(JSON.stringify(payload), '*');
    } catch (error) {
      void error;
    }
  },
  pause: function (frame) {
    window.MgVideoFrame.setPlaying(frame, false);
  },
  setPlaying: function (frame, playing) {
    window.MgVideoFrame.send(frame, { method: playing ? 'play' : 'pause' }, playing ? 'playVideo' : 'pauseVideo');
  },
  setMuted: function (frame, muted) {
    window.MgVideoFrame.send(frame, { method: 'setVolume', value: muted ? 0 : 1 }, muted ? 'mute' : 'unMute');
  }
};

if (!customElements.get('mg-product-gallery')) {
  /* The gallery and the viewer are the same thing twice: a horizontal
     scroll-snap track whose current item is read back from its scroll offset.
     Written out twice it drifted apart the moment one of them needed a fix. */
  var SnapTrack = {
    goTo: function (el, index, smooth) {
      if (!el) return;
      var width = el.clientWidth;
      if (!width) return;
      el.scrollTo({ left: index * width, behavior: smooth ? 'smooth' : 'auto' });
    },
    indexFrom: function (el, count) {
      if (!el) return 0;
      var width = el.clientWidth;
      if (!width) return 0;
      return Math.max(0, Math.min(Math.round(el.scrollLeft / width), count - 1));
    }
  };

  /* Pinch, wheel, double press and drag over one picture. Zoom keeps the point
     under the finger or cursor still; the box is read once per gesture and the
     transform written once per frame. */
  var TAP = { slop: 10, hold: 250, gap: 300, near: 30 };

  function ZoomSurface(root, resolveImage) {
    this.root = root;
    this.resolveImage = resolveImage;
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this._pointers = new Map();
  }

  ZoomSurface.prototype = {
    /* Never magnify past what the master file resolves (data-full-width). */
    maxScale: function (img) {
      if (this.scale === 1) {
        var atRest = img.getBoundingClientRect().width;
        if (atRest) this._fittedWidth = atRest;
      }
      var full = +img.getAttribute('data-full-width') || img.naturalWidth;
      if (!full || !this._fittedWidth) return 2.5;
      return Math.max(1.5, Math.min(4, full / (this._fittedWidth * (window.devicePixelRatio || 1))));
    },

    /* The viewer opens on a screen-sized file; the master is decoded aside on
       the first zoom, so the swap never stalls a frame. */
    loadFull: function (img) {
      var src = img.getAttribute('data-zoom-src');
      if (!src) return;
      img.removeAttribute('data-zoom-src');
      var hi = new Image();
      hi.src = src;
      var swap = function () { img.removeAttribute('srcset'); img.src = src; };
      (hi.decode ? hi.decode() : Promise.reject()).then(swap, function () {
        if (hi.naturalWidth) swap();
        else hi.onload = swap;
      });
    },

    reset: function () {
      var img = this.resolveImage();
      this.scale = 1;
      this.x = this.y = 0;
      this._pointers.clear();
      /* The next medium is a different picture. */
      this._pinch = this._panFrom = this._tap = this._box = null;
      this._fittedWidth = 0;
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = 0;
      if (!img) return;
      img.style.transform = '';
      img.removeAttribute('data-zoomed');
      img.removeAttribute('data-panning');
    },

    /* Centre of the box at rest, its size and the frame around it. */
    measure: function (img) {
      var r = img.getBoundingClientRect();
      var f = (img.closest('.mg-gallery__lb-slide') || img).getBoundingClientRect();
      this._box = { cx: r.left + r.width / 2 - this.x, cy: r.top + r.height / 2 - this.y,
        w: img.offsetWidth, h: img.offsetHeight, fw: f.width, fh: f.height, max: this.maxScale(img) };
      return this._box;
    },

    render: function (img) {
      var b = this._box;
      if (this.scale <= 1.001) {
        this.scale = 1;
        this.x = this.y = 0;
      } else if (b) {
        /* Clamp so the picture never leaves its own frame. */
        var ox = Math.max(0, (b.w * this.scale - b.fw) / 2);
        var oy = Math.max(0, (b.h * this.scale - b.fh) / 2);
        this.x = Math.max(-ox, Math.min(ox, this.x));
        this.y = Math.max(-oy, Math.min(oy, this.y));
      }
      this._img = img;
      if (this._raf) return;
      var self = this;
      this._raf = requestAnimationFrame(function () {
        var el = self._img;
        self._raf = 0;
        if (self.scale === 1) {
          el.style.transform = '';
          el.removeAttribute('data-zoomed');
          return;
        }
        el.setAttribute('data-zoomed', 'true');
        self.loadFull(el);
        el.style.transform = 'translate3d(' + self.x + 'px, ' + self.y + 'px, 0) scale(' + self.scale + ')';
      });
    },

    /* Scale to s around the screen point (px, py), the centre when omitted. A
       discrete step glides; pinch and drag follow the finger. */
    zoomTo: function (s, px, py, glide) {
      var img = this.resolveImage();
      if (!img) return;
      var b = this._box || this.measure(img);
      var next = Math.max(1, Math.min(b.max, s));
      var rx = px == null ? 0 : px - b.cx;
      var ry = py == null ? 0 : py - b.cy;
      this.x = rx - (next / this.scale) * (rx - this.x);
      this.y = ry - (next / this.scale) * (ry - this.y);
      this.scale = next;
      if (glide) {
        img.setAttribute('data-gliding', '');
        img.addEventListener('transitionend', function () { img.removeAttribute('data-gliding'); }, { once: true });
      }
      this.render(img);
    },

    _mid: function () {
      var a = Array.from(this._pointers.values());
      return { x: (a[0].x + a[1].x) / 2, y: (a[0].y + a[1].y) / 2, d: Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) };
    },

    attach: function () {
      var self = this;
      var up = function (e) {
        if (!self._pointers.delete(e.pointerId)) return;
        if (self._pointers.size < 2) self._pinch = null;
        if (self._pointers.size) return;
        self._panFrom = null;
        var img = self.resolveImage();
        if (img) img.removeAttribute('data-panning');
        /* Touch double tap: two short, close taps toggle the zoom on that point. */
        var t = self._tap;
        if (!t || !img || e.type !== 'pointerup' || e.pointerType === 'mouse' ||
            Math.hypot(e.clientX - t.x, e.clientY - t.y) > TAP.slop || e.timeStamp - t.t > TAP.hold) {
          self._tap = null;
          return;
        }
        var p = t.prev;
        if (p && t.t - p.t < TAP.gap && Math.hypot(t.x - p.x, t.y - p.y) < TAP.near) {
          self._tap = null;
          self.zoomTo(self.scale > 1 ? 1 : 2.5, t.x, t.y, true);
        } else {
          t.done = true;
        }
      };

      this._handlers = {
        wheel: function (e) {
          if (!e.target.closest('[data-lb-zoomable]')) return;
          e.preventDefault();
          self.zoomTo(self.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15), e.clientX, e.clientY);
        },
        /* Mouse only: a touch double tap is read from the pointers. */
        dblclick: function (e) {
          var img = e.target.closest('[data-lb-zoomable]');
          if (!img || self._lastType !== 'mouse') return;
          self.measure(img);
          self.zoomTo(self.scale > 1 ? 1 : 2.5, e.clientX, e.clientY, true);
        },
        pointerdown: function (e) {
          var img = e.target.closest('[data-lb-zoomable]');
          if (!img) return;
          self._lastType = e.pointerType;
          img.removeAttribute('data-gliding');
          if (!self._pointers.size) self.measure(img);
          self._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (self._pointers.size === 2) {
            var m = self._mid();
            self._pinch = { d: m.d, s: self.scale, x: self.x, y: self.y, mx: m.x - self._box.cx, my: m.y - self._box.cy };
            self._panFrom = null;
            img.setAttribute('data-panning', 'true');
            return;
          }
          if (self._pointers.size > 1) return;
          var last = self._tap;
          self._tap = { x: e.clientX, y: e.clientY, t: e.timeStamp, prev: last && last.done ? last : null };
          if (last) last.prev = null;
          if (self.scale > 1) {
            self._panFrom = { x: e.clientX, y: e.clientY, ox: self.x, oy: self.y };
            img.setAttribute('data-panning', 'true');
            img.setPointerCapture(e.pointerId);
          }
        },
        pointermove: function (e) {
          if (!self._pointers.has(e.pointerId)) return;
          self._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          var img = self.resolveImage();
          var b = self._box;
          var p = self._pinch;
          if (!img || !b) return;
          if (self._pointers.size === 2 && p) {
            var m = self._mid();
            var s = Math.max(1, Math.min(b.max, p.s * m.d / p.d));
            self.x = m.x - b.cx - (s / p.s) * (p.mx - p.x);
            self.y = m.y - b.cy - (s / p.s) * (p.my - p.y);
            self.scale = s;
          } else if (self._pointers.size === 1 && self._panFrom && self.scale > 1) {
            self.x = self._panFrom.ox + (e.clientX - self._panFrom.x);
            self.y = self._panFrom.oy + (e.clientY - self._panFrom.y);
          } else {
            return;
          }
          self.render(img);
          e.preventDefault();
        },
        pointerup: up,
        pointercancel: up
      };
      Object.keys(this._handlers).forEach(function (type) {
        self.root.addEventListener(type, self._handlers[type], { passive: false });
      });
    },

    detach: function () {
      var self = this;
      if (!this.root || !this._handlers) return;
      Object.keys(this._handlers).forEach(function (type) {
        self.root.removeEventListener(type, self._handlers[type], { passive: false });
      });
      if (this._raf) cancelAnimationFrame(this._raf);
    }
  };

  class MgProductGallery extends HTMLElement {
    connectedCallback() {
      this.mainContainer = this.querySelector('[data-gallery-main]');
      if (!this.mainContainer) return;
      if (this._initialized) return;
      this._initialized = true;

      this.slides = Array.from(this.mainContainer.querySelectorAll('[data-media-index]'));
      this.thumbsContainer = this.querySelector('[data-gallery-thumbs]');
      this.thumbs = this.thumbsContainer
        ? Array.from(this.thumbsContainer.querySelectorAll('[data-thumb-index]'))
        : [];
      this._dotsContainer = this.querySelector('[data-gallery-dots]');
      this.dots = this._dotsContainer
        ? Array.from(this._dotsContainer.querySelectorAll('[data-dot-index]'))
        : [];
      this.counter = this.querySelector('[data-gallery-current]');
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.currentIndex = 0;
      this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._bindThumbnails();
      this._bindDots();
      this._bindArrows();
      this._bindScroll();
      this._bindVariantChange();
      this._bindHeightSync();
      this._bindLightbox();
      this._updateActive(0);
    }

    disconnectedCallback() {
      this._initialized = false;
      if (this._scrollRaf) {
        cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = null;
      }
      if (this._onScroll && this.mainContainer) {
        this.mainContainer.removeEventListener('scroll', this._onScroll);
      }
      if (this._onThumbClick && this.thumbsContainer) {
        this.thumbsContainer.removeEventListener('click', this._onThumbClick);
      }
      if (this._onDotsClick && this._dotsContainer) {
        this._dotsContainer.removeEventListener('click', this._onDotsClick);
      }
      if (this._onPrev && this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this._onNext && this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      if (this._variantHandler && window.MgEvents) {
        window.MgEvents.unsubscribe(MgEvents.Events.PRODUCT_VARIANT_CHANGED, this._variantHandler);
      }
      if (this._onHeightChange) {
        window.removeEventListener('resize', this._onHeightChange);
        if (this.mainContainer) {
          this.mainContainer.removeEventListener('load', this._onHeightChange, true);
        }
      }
      if (this._trackObserver) {
        this._trackObserver.disconnect();
        this._trackObserver = null;
      }
      if (this._onZoom && this.zoomBtn) this.zoomBtn.removeEventListener('click', this._onZoom);
      if (this._onSlideClick && this.mainContainer) {
        this.mainContainer.removeEventListener('click', this._onSlideClick);
      }
      if (this._lbScrollRaf) {
        cancelAnimationFrame(this._lbScrollRaf);
        this._lbScrollRaf = null;
      }
      if (this._onLbScroll && this.lbTrack) {
        this.lbTrack.removeEventListener('scroll', this._onLbScroll);
      }
      if (this.lightbox) {
        if (this._onLbClick) this.lightbox.removeEventListener('click', this._onLbClick);
        if (this._onLbKeydown) this.lightbox.removeEventListener('keydown', this._onLbKeydown);
        if (this._onLbClose) this.lightbox.removeEventListener('close', this._onLbClose);
      }
      if (this.zoomSurface) this.zoomSurface.detach();
    }

    /* ── Active state derived from scroll position (no IntersectionObserver) ── */
    _bindScroll() {
      this._onScroll = function () {
        if (this._scrollRaf) return;
        this._scrollRaf = requestAnimationFrame(function () {
          this._scrollRaf = null;
          var idx = SnapTrack.indexFrom(this.mainContainer, this.slides.length);
          if (idx !== this.currentIndex) this._updateActive(idx);
        }.bind(this));
      }.bind(this);
      this.mainContainer.addEventListener('scroll', this._onScroll, { passive: true });
    }

    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;
      SnapTrack.goTo(this.mainContainer, index, !this._reduceMotion);
    }

    _updateActive(index) {
      this.currentIndex = index;

      this.slides.forEach(function (s, i) {
        s.classList.toggle('mg-gallery__slide--active', i === index);
        if (i !== index) this._pauseSlideMedia(s);
      }, this);

      this.thumbs.forEach(function (t, i) {
        var on = i === index;
        t.classList.toggle('mg-gallery__thumb--active', on);
        if (on) {
          t.setAttribute('aria-current', 'true');
        } else {
          t.removeAttribute('aria-current');
        }
      });
      if (this.thumbs[index]) this._scrollThumbIntoStrip(this.thumbs[index]);

      this.dots.forEach(function (d, i) {
        var on = i === index;
        d.classList.toggle('mg-gallery__dot--active', on);
        if (on) {
          d.setAttribute('aria-current', 'true');
        } else {
          d.removeAttribute('aria-current');
        }
      });

      this.syncTrackHeight();

      if (this.counter) this.counter.textContent = String(index + 1);
      if (this.prevBtn) this.prevBtn.disabled = index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = index >= this.slides.length - 1;
    }

    /* Scroll the strip only, never the window (scrollIntoView would move the page);
       both axes, for horizontal and vertical strips. */
    _scrollThumbIntoStrip(thumb) {
      var c = this.thumbsContainer;
      if (!c || !thumb) return;
      var cr = c.getBoundingClientRect();
      var tr = thumb.getBoundingClientRect();
      var dx = 0;
      var dy = 0;
      if (tr.left < cr.left) dx = tr.left - cr.left;
      else if (tr.right > cr.right) dx = tr.right - cr.right;
      if (tr.top < cr.top) dy = tr.top - cr.top;
      else if (tr.bottom > cr.bottom) dy = tr.bottom - cr.bottom;
      if (!dx && !dy) return;
      c.scrollBy({
        left: dx,
        top: dy,
        behavior: this._reduceMotion ? 'auto' : 'smooth'
      });
    }

    _bindThumbnails() {
      if (!this.thumbsContainer) return;
      this._onThumbClick = function (e) {
        var thumb = e.target.closest('[data-thumb-index]');
        if (!thumb) return;
        var index = parseInt(thumb.dataset.thumbIndex, 10);
        if (!isNaN(index)) this.goToSlide(index);
      }.bind(this);
      this.thumbsContainer.addEventListener('click', this._onThumbClick);
    }

    _bindDots() {
      if (!this._dotsContainer) return;
      this._onDotsClick = function (e) {
        var dot = e.target.closest('[data-dot-index]');
        if (!dot) return;
        var index = parseInt(dot.dataset.dotIndex, 10);
        if (!isNaN(index)) this.goToSlide(index);
      }.bind(this);
      this._dotsContainer.addEventListener('click', this._onDotsClick);
    }

    _bindArrows() {
      if (this.prevBtn) {
        this._onPrev = function () {
          this.goToSlide(this.currentIndex - 1);
        }.bind(this);
        this.prevBtn.addEventListener('click', this._onPrev);
      }
      if (this.nextBtn) {
        this._onNext = function () {
          this.goToSlide(this.currentIndex + 1);
        }.bind(this);
        this.nextBtn.addEventListener('click', this._onNext);
      }
    }

    _bindVariantChange() {
      if (!window.MgEvents) return;
      this._variantHandler = function (data) {
        if (!data || !data.variant) return;
        var fm = data.variant.featured_media;
        var id = fm ? fm.id : null;
        if (!id) return;
        var target = this.mainContainer.querySelector('[data-media-id="' + id + '"]');
        if (!target) return;
        var index = parseInt(target.dataset.mediaIndex, 10);
        if (!isNaN(index)) this.goToSlide(index);
      }.bind(this);
      window.MgEvents.subscribe(MgEvents.Events.PRODUCT_VARIANT_CHANGED, this._variantHandler);
    }

    /* ── Track height follows the slide on screen ── It only changes after the
       shopper's own navigation, which CLS does not count. */
    syncTrackHeight() {
      var active = this.slides[this.currentIndex];
      if (!active || !this.mainContainer) return;
      var height = Math.round(active.getBoundingClientRect().height);
      if (!height) return;

      if (this._heightPrimed) {
        this.mainContainer.style.blockSize = height + 'px';
        return;
      }

      /* `auto` cannot be interpolated: prime the first value without the transition. */
      this.mainContainer.style.transition = 'none';
      this.mainContainer.style.blockSize = height + 'px';
      void this.mainContainer.offsetHeight;
      this.mainContainer.style.removeProperty('transition');
      this._heightPrimed = true;
    }

    /* Media arrives late — lazy images, and the player that replaces a poster.
       Re-measure once it lands, or the track keeps the old slide's height. */
    _bindHeightSync() {
      this._onHeightChange = function () {
        this.syncTrackHeight();
      }.bind(this);
      window.addEventListener('resize', this._onHeightChange);
      this.mainContainer.addEventListener('load', this._onHeightChange, true);

      if (typeof ResizeObserver === 'function') {
        this._trackObserver = new ResizeObserver(this._onHeightChange);
        this.slides.forEach(function (slide) {
          this._trackObserver.observe(slide);
        }, this);
      }
    }

    /* A video the shopper swiped away from must not keep playing off-screen. */
    _pauseSlideMedia(slide) {
      var video = slide.querySelector('video');
      if (video && !video.paused) video.pause();

      window.MgVideoFrame.pause(slide.querySelector('iframe'));
    }

    /* ── Lightbox ── same scroll-snap primitive as the gallery, every medium reachable. */
    _bindLightbox() {
      if (this.dataset.zoomMode !== 'lightbox') return;
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      if (!this.lightbox) return;
      this.lbTrack = this.lightbox.querySelector('[data-lb-track]');
      this.lbSlides = this.lbTrack
        ? Array.from(this.lbTrack.querySelectorAll('[data-lb-index]'))
        : [];
      if (!this.lbSlides.length) return;
      this.lbCounter = this.lightbox.querySelector('[data-lb-current]');
      this.zoomBtn = this.querySelector('[data-gallery-zoom]');
      this._lbIndex = 0;
      this.zoomSurface = new ZoomSurface(this.lightbox, this._currentZoomImage.bind(this));

      var openAt = function (index) {
        this._lbReturnFocus = document.activeElement;
        this._lbIndex = index;
        /* While its own file loads, the viewer shows the gallery's picture, already in cache. */
        var lbImg = this.lbSlides[index] && this.lbSlides[index].querySelector('img');
        var shown = this.slides && this.slides[index] && this.slides[index].querySelector('img');
        if (lbImg && shown && !lbImg.complete && shown.currentSrc) lbImg.style.background = 'center / contain no-repeat url("' + shown.currentSrc + '")';
        if (typeof this.lightbox.showModal === 'function') {
          this.lightbox.showModal();
        } else {
          this.lightbox.setAttribute('open', '');
        }
        /* The track has no width until the dialog is in the top layer, so the
           jump to the right medium has to wait a frame. */
        requestAnimationFrame(function () {
          this._lbScrollTo(index, true);
          this._lbSync();
          /* Facades observed while the dialog was hidden had no box: re-scan them now. */
          if (window.MgGalleryAutoplay) window.MgGalleryAutoplay.scan(this.lightbox);
        }.bind(this));
      }.bind(this);

      if (this.zoomBtn) {
        this._onZoom = function () {
          openAt(this.currentIndex);
        }.bind(this);
        this.zoomBtn.addEventListener('click', this._onZoom);
      }

      this._onSlideClick = function (e) {
        if (e.target.closest('[data-gallery-prev], [data-gallery-next], [data-gallery-zoom]')) return;
        /* A press on a video is a press on its player, never a zoom. */
        if (e.target.closest('[data-gallery-facade], [data-gallery-player]')) return;
        var slide = e.target.closest('[data-media-index]');
        if (!slide) return;
        var index = parseInt(slide.dataset.mediaIndex, 10);
        if (!isNaN(index)) openAt(index);
      }.bind(this);
      this.mainContainer.addEventListener('click', this._onSlideClick);

      this._onLbClick = function (e) {
        if (e.target.closest('[data-lb-close]')) {
          this.lightbox.close();
          return;
        }
        if (e.target.closest('[data-lb-prev]')) {
          this._lbNav(-1);
          return;
        }
        if (e.target.closest('[data-lb-next]')) {
          this._lbNav(1);
          return;
        }
        /* Backdrop press closes — but only on the empty margin around the
           medium, never on the medium itself or a control. */
        if (e.target === this.lightbox || e.target.classList.contains('mg-gallery__lb-slide')) {
          if (this.zoomSurface.scale === 1) this.lightbox.close();
        }
      }.bind(this);
      this.lightbox.addEventListener('click', this._onLbClick);

      this._onLbKeydown = function (e) {
        if (e.key === 'ArrowLeft') this._lbNav(-1);
        else if (e.key === 'ArrowRight') this._lbNav(1);
      }.bind(this);
      this.lightbox.addEventListener('keydown', this._onLbKeydown);

      this._onLbScroll = function () {
        if (this._lbScrollRaf) return;
        this._lbScrollRaf = requestAnimationFrame(function () {
          this._lbScrollRaf = null;
          this._lbSync();
        }.bind(this));
      }.bind(this);
      this.lbTrack.addEventListener('scroll', this._onLbScroll, { passive: true });

      /* Closing hands focus back to whatever opened the viewer. */
      this._onLbClose = function () {
        this.zoomSurface.reset();
        window.MgVideoFrame.pause(this.lightbox.querySelector('iframe'));
        var video = this.lightbox.querySelector('video');
        if (video && !video.paused) video.pause();
        if (this._lbReturnFocus && typeof this._lbReturnFocus.focus === 'function') {
          this._lbReturnFocus.focus();
        }
      }.bind(this);
      this.lightbox.addEventListener('close', this._onLbClose);

      this.zoomSurface.attach();
    }

    /* The picture the zoom acts on: only the gallery knows where the viewer is. */
    _currentZoomImage() {
      var slide = this.lbSlides[this._lbIndex];
      return slide ? slide.querySelector('[data-lb-zoomable]') : null;
    }

    _lbScrollTo(index, instant) {
      SnapTrack.goTo(this.lbTrack, index, !instant && !this._reduceMotion);
    }

    _lbSync() {
      if (!this.lbTrack) return;
      var index = SnapTrack.indexFrom(this.lbTrack, this.lbSlides.length);
      if (index === this._lbIndex) return;
      this._lbIndex = index;
      this.zoomSurface.reset();
      if (this.lbCounter) this.lbCounter.textContent = String(index + 1);
      /* The gallery underneath follows, so closing lands on the same medium. */
      if (index !== this.currentIndex) this.goToSlide(index);
    }

    _lbNav(dir) {
      var next = (this._lbIndex + dir + this.lbSlides.length) % this.lbSlides.length;
      this._lbScrollTo(next);
    }

  }

  customElements.define('mg-product-gallery', MgProductGallery);
}

/* ── Video facade activation: poster → real player, muted and already playing ──
   Delegated to the document: the grid layout renders facades without
   <mg-product-gallery>. The player sits in an inert <template> until then. */
if (!window.__namyGalleryFacadeInit) {
  window.__namyGalleryFacadeInit = true;

  /* The video owns its state and the button follows it, whatever stopped it.
     Media events do not bubble, hence the capture phase. */
  function syncToggleState(video) {
    var player = video.closest('[data-gallery-player]');
    if (!player) return;
    var btn = player.querySelector('[data-player-toggle]');
    if (!btn) return;
    var playing = !video.paused;
    btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    var label = playing ? btn.dataset.labelPause : btn.dataset.labelPlay;
    if (label) btn.setAttribute('aria-label', label);
  }

  ['play', 'pause', 'ended'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (e.target && e.target.tagName === 'VIDEO') syncToggleState(e.target);
    }, true);
  });

  /* Play / pause, required for a self-starting loop (WCAG 2.2.2). Icons and
     label both ride on aria-pressed. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-player-toggle]');
    if (!btn) return;
    var player = btn.closest('[data-gallery-player]');
    if (!player) return;

    var wasPlaying = btn.getAttribute('aria-pressed') !== 'false';
    var video = player.querySelector('video');
    if (video) {
      /* Ask the video, not the button: the element knows whether it is
         actually running, the attribute is only a reflection of it. */
      if (!video.paused) {
        video.pause();
      } else {
        var resumed = video.play();
        if (resumed && resumed.catch) {
          resumed.catch(function (error) {
            void error;
          });
        }
      }
      /* No manual attribute write here: the play/pause events above own it. */
    } else {
      /* A hosted player answers over postMessage and reports nothing back, so
         its button is the only record of the state — write it by hand. */
      window.MgVideoFrame.setPlaying(player.querySelector('iframe'), !wasPlaying);
      btn.setAttribute('aria-pressed', wasPlaying ? 'false' : 'true');
      var label = wasPlaying ? btn.dataset.labelPlay : btn.dataset.labelPause;
      if (label) btn.setAttribute('aria-label', label);
    }
  });

  /* Sound is the only decision left to the shopper, so it is the only button. */
  document.addEventListener('click', function (e) {
    var muteBtn = e.target.closest('[data-player-mute]');
    if (!muteBtn) return;
    var player = muteBtn.closest('[data-gallery-player]');
    if (!player) return;

    var wasMuted = muteBtn.getAttribute('aria-pressed') !== 'false';
    var video = player.querySelector('video');
    if (video) {
      video.muted = !wasMuted;
      /* Unmuting is a gesture-blessed moment: if the clip had been paused by a
         slide change, the shopper expects sound AND motion. */
      if (!video.muted && video.paused) {
        var resumed = video.play();
        if (resumed && resumed.catch) {
          resumed.catch(function (error) {
            void error;
          });
        }
      }
    } else {
      window.MgVideoFrame.setMuted(player.querySelector('iframe'), !wasMuted);
    }
    muteBtn.setAttribute('aria-pressed', wasMuted ? 'false' : 'true');
  });

  /* No control bar means no play button, so the frame itself is the target —
     the affordance every video on a phone already teaches. */
  document.addEventListener('click', function (e) {
    var video = e.target.closest('[data-gallery-player] video');
    if (!video) return;
    if (video.paused) {
      var started = video.play();
      if (started && started.catch) {
        started.catch(function (error) {
          void error;
        });
      }
    } else {
      video.pause();
    }
  });

  /* ── Auto-start when the medium is actually on screen ──
     Never on page load: each video waits until its slide is the one in view,
     in the carousel, the grid and the viewer alike. */
  function autoStartFacades(scope) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var play = entry.target.querySelector('[data-facade-play]');
        observer.unobserve(entry.target);
        if (play) play.click();
      });
    }, { threshold: 0.6 });

    (scope || document).querySelectorAll('[data-gallery-facade]').forEach(function (facade) {
      observer.observe(facade);
    });
    return observer;
  }

  /* Anything injected later — a viewer opening, a section re-rendering — gets
     the same treatment. */
  window.MgGalleryAutoplay = { scan: autoStartFacades };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      autoStartFacades();
    });
  } else {
    autoStartFacades();
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-facade-play]');
    if (!trigger) return;
    var facade = trigger.closest('[data-gallery-facade]');
    if (!facade) return;
    var tpl = facade.querySelector('[data-facade-media]');
    if (!tpl || !tpl.content) return;

    /* The poster's ratio keeps the slide height when the player replaces it. */
    var slide = facade.closest('[data-media-index]');
    if (slide && facade.dataset.facadeAspect) {
      slide.style.setProperty('--mg-gallery-aspect', facade.dataset.facadeAspect);
    }

    var fragment = tpl.content.cloneNode(true);
    var video = fragment.querySelector('video');
    if (video) {
      /* No download or playback-rate entry in Chrome's menu on a bare <video>. */
      video.setAttribute('controlsList', 'nodownload noplaybackrate');
      video.setAttribute('playsinline', '');
      video.disablePictureInPicture = true;
    }

    facade.replaceWith(fragment);

    if (video && typeof video.play === 'function') {
      var started = video.play();
      if (started && started.catch) {
        /* A refused play is survivable: the controls are already visible, so
           the shopper presses play themselves. */
        started.catch(function (error) {
          void error;
        });
      }
    }
  });
}
